import Paper from "@mui/material/Paper";
import classes from "../../styles/DashboardView.module.css";
import { numberWithCommas } from "../Oversigt";
import { ForecastSummary, ConfidenceBucket } from "../../utilities/forecast";

// ------------------------------------------------------------------
// ForecastKpiCards — 3-card hero row for the Prognose tab.
//
// Matches the Statistik tab's KPI card aesthetic (same Paper, same
// blue primary, same tabular-nums, same trend arrow semantics) so
// the two tabs feel like parts of one product.
// ------------------------------------------------------------------

interface ForecastKpiCardsProps {
    revenue: ForecastSummary | null;
    registrations: ForecastSummary | null;
    confidence: ConfidenceBucket;
    /** Use the worst of the two band widths so the "Sikkerhed" card reflects
     *  the weakest forecast on the page. */
    worstBandKr: number;
}

type Trend = "up" | "down" | "flat" | "none";

function trendFromDelta(delta: number | null): Trend {
    if (delta === null) return "none";
    if (delta > 0.02) return "up";
    if (delta < -0.02) return "down";
    return "flat";
}

function arrow(t: Trend): string {
    if (t === "up") return "▲";
    if (t === "down") return "▼";
    return "–";
}

function formatPct(delta: number | null): string {
    if (delta === null) return "—";
    const pct = Math.round(delta * 100);
    const sign = pct > 0 ? "+" : "";
    return `${sign}${pct}%`;
}

/** Format millions (DKK) to compact form: 14.2M. Counts shown as-is with thousands separator. */
function formatCompactKr(n: number): string {
    if (n >= 1_000_000) {
        const m = n / 1_000_000;
        return `${m.toFixed(1).replace(".", ",")}M`;
    }
    if (n >= 10_000) {
        const k = Math.round(n / 1000);
        return `${numberWithCommas(k)}k`;
    }
    return String(numberWithCommas(n) ?? 0);
}

function formatCompactBand(n: number): string {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(".", ",")}M kr`;
    if (n >= 10_000) return `${Math.round(n / 1000)}k kr`;
    return `${numberWithCommas(n) ?? 0} kr`;
}

export function ForecastKpiCards({ revenue, registrations, confidence, worstBandKr }: ForecastKpiCardsProps) {
    const revTrend = trendFromDelta(revenue?.yoyDelta ?? null);
    const regTrend = trendFromDelta(registrations?.yoyDelta ?? null);

    return (
        <div className={classes.kpiGrid} style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
            {/* Revenue 12mo */}
            <Paper className={`${classes.kpiCard} ${classes[`trend_${revTrend}`]}`} elevation={2}>
                <div className={classes.kpiValueRow}>
                    <span className={classes.kpiValue}>{revenue ? formatCompactKr(revenue.forecastTotal) : "—"}</span>
                    <span className={classes.kpiUnit}>kr</span>
                </div>
                <div className={classes.kpiLabel}>Forventet omsætning i år</div>
                {revenue && (
                    <div className={classes.kpiCompare}>
                        <span className={classes.kpiDelta}>
                            <span className={classes.kpiArrow}>{arrow(revTrend)}</span>
                            <span>{formatPct(revenue.yoyDelta)}</span>
                        </span>
                        <span className={classes.kpiIndex}>vs. sidste år</span>
                    </div>
                )}
            </Paper>

            {/* Registrations 12mo */}
            <Paper className={`${classes.kpiCard} ${classes[`trend_${regTrend}`]}`} elevation={2}>
                <div className={classes.kpiValueRow}>
                    <span className={classes.kpiValue}>{registrations ? numberWithCommas(registrations.forecastTotal) : "—"}</span>
                    <span className={classes.kpiUnit}>stk</span>
                </div>
                <div className={classes.kpiLabel}>Forventede tilmeldinger i år</div>
                {registrations && (
                    <div className={classes.kpiCompare}>
                        <span className={classes.kpiDelta}>
                            <span className={classes.kpiArrow}>{arrow(regTrend)}</span>
                            <span>{formatPct(registrations.yoyDelta)}</span>
                        </span>
                        <span className={classes.kpiIndex}>vs. sidste år</span>
                    </div>
                )}
            </Paper>

            {/* Confidence */}
            <Paper className={classes.kpiCard} elevation={2}>
                <div className={classes.kpiValueRow}>
                    <span className={classes.kpiValue} style={{ color: confidenceColor(confidence) }}>{confidence}</span>
                </div>
                <div className={classes.kpiLabel}>Sikkerhed i prognose</div>
                <div className={classes.kpiCompare}>
                    <span className={classes.kpiIndex}>± {formatCompactBand(worstBandKr)} (80% interval)</span>
                </div>
            </Paper>
        </div>
    );
}

function confidenceColor(c: ConfidenceBucket): string {
    // Match Statistik semantic colors.
    if (c === "Høj") return "#1b8a5a";    // green
    if (c === "Middel") return "#8a7a3d"; // amber-ish
    return "#c53a31";                      // red
}
